import React, { useRef, useState } from 'react';
import { calculateGates } from '../../utils/karmaCalc';

const VIEW_W = 600;
const VIEW_H = 700;

const SHAPE_OPTIONS = [
  { value: 'door', label: 'Qapı' },
  { value: 'arch', label: '🏛️ Arka qapı (cənnət)' },
  { value: 'portal', label: '✨ İşıq portalı' },
  { value: 'columns', label: '🌟 Sütunlar + arka' },
  { value: 'circle', label: 'Dairə' },
  { value: 'square', label: 'Kvadrat' },
  { value: 'star', label: 'Ulduz' },
  { value: 'hexagon', label: 'Altıbucaq' },
  { value: 'diamond', label: 'Almaz' },
  { value: 'triangle', label: 'Üçbucaq' },
  { value: 'heart', label: 'Ürək' },
  { value: 'none', label: 'Görünməz (yalnız rəqəm)' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GatesEditor = ({ gates, nodeLabels, backgroundLayers, onChange }) => {
  const wrapRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [previewDate, setPreviewDate] = useState('18.04.1991');

  const g = gates || {
    enabled: false,
    names: ['0-30', '30-39', '39-48', '48+'],
    positions_left: [{ x: 30, y: 130 }, { x: 30, y: 220 }, { x: 30, y: 320 }, { x: 30, y: 420 }],
    positions_right: [{ x: 570, y: 130 }, { x: 570, y: 220 }, { x: 570, y: 320 }, { x: 570, y: 420 }],
    size: 28,
    color: '#fbbf24',
    show_left: true,
    show_right: true,
    shapes: ['door', 'door', 'door', 'door'],
    sizes: [null, null, null, null],
    value_sources: ['auto', 'auto', 'auto', 'auto'],
  };

  const update = (patch) => onChange({ ...g, ...patch });

  // Build value_source dropdown options from node labels
  const sourceOptions = (() => {
    const opts = [{ value: 'auto', label: 'Avtomatik (qapı dəyəri)' }];
    Object.entries(nodeLabels || {}).forEach(([id, info]) => {
      const letter = info?.letter || id;
      opts.push({ value: id, label: `${letter} (${id} əsas)` });
      opts.push({ value: `${id}1`, label: `${letter}1 (${id} əlavə)` });
    });
    return opts;
  })();

  const previewValues = (() => {
    const m = previewDate.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!m) return null;
    return calculateGates(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10));
  })();

  const onPointerDown = (side, idx) => (e) => {
    e.preventDefault();
    setDragging({ side, idx });
  };
  const onPointerMove = (e) => {
    if (!dragging || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const x = Math.max(15, Math.min(VIEW_W - 15, (cx / rect.width) * VIEW_W));
    const y = Math.max(15, Math.min(VIEW_H - 15, (cy / rect.height) * VIEW_H));
    const key = dragging.side === 'left' ? 'positions_left' : 'positions_right';
    const arr = [...(g[key] || [])];
    arr[dragging.idx] = { x: Math.round(x), y: Math.round(y) };
    update({ [key]: arr });
  };
  const onPointerUp = () => setDragging(null);

  const renderPreviewGate = (side, pos, idx) => {
    if (!pos) return null;
    const baseR = g.size || 28;
    const r = (g.sizes && g.sizes[idx]) || baseR;
    const shape = (g.shapes && g.shapes[idx]) || 'door';
    const name = (g.names || [])[idx] || `Q${idx + 1}`;
    const val = previewValues ? previewValues[idx] : null;
    const baseProps = {
      style: { cursor: 'grab' },
      onPointerDown: onPointerDown(side, idx),
      'data-testid': `gate-edit-${side}-${idx}`,
    };

    if (shape === 'none') {
      return (
        <g key={`${side}-${idx}`} {...baseProps}>
          <circle cx={pos.x} cy={pos.y} r={r * 1.4} fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
          <text x={pos.x} y={pos.y - r * 0.4} textAnchor="middle" dominantBaseline="central" fill={g.color} fontSize={r * 0.45} fontWeight="700">{name}</text>
          {val != null && (
            <text x={pos.x} y={pos.y + r * 0.4} textAnchor="middle" dominantBaseline="central" fill="white" fontSize={r * 1.0} fontWeight="800">{val}</text>
          )}
        </g>
      );
    }
    if (shape === 'door') {
      return (
        <g key={`${side}-${idx}`} {...baseProps}>
          <rect x={pos.x - r} y={pos.y - r * 1.4} width={r * 2} height={r * 2.8}
            rx={r * 0.18} fill={g.color} stroke="#fff" strokeWidth="0.8" opacity="0.85" />
          <rect x={pos.x - r * 0.85} y={pos.y - r * 1.25} width={r * 1.7} height={r * 2.5}
            rx={r * 0.12} fill="rgba(0,0,0,0.5)" />
          <text x={pos.x} y={pos.y - r * 0.55} textAnchor="middle" dominantBaseline="central" fill={g.color} fontSize={r * 0.4} fontWeight="600">{name}</text>
          {val != null && (
            <text x={pos.x} y={pos.y + r * 0.4} textAnchor="middle" dominantBaseline="central" fill="white" fontSize={r * 0.85} fontWeight="800">{val}</text>
          )}
        </g>
      );
    }
    // Generic shape (rendered as a circle preview for simplicity)
    return (
      <g key={`${side}-${idx}`} {...baseProps}>
        <circle cx={pos.x} cy={pos.y} r={r} fill={g.color} stroke="#fff" strokeWidth="0.8" opacity="0.85" />
        <text x={pos.x} y={pos.y - r * 0.32} textAnchor="middle" dominantBaseline="central" fill="rgba(0,0,0,0.7)" fontSize={r * 0.36} fontWeight="700">{name}</text>
        {val != null && (
          <text x={pos.x} y={pos.y + r * 0.3} textAnchor="middle" dominantBaseline="central" fill="white" fontSize={r * 0.7} fontWeight="800">{val}</text>
        )}
      </g>
    );
  };

  // Helpers to update parallel arrays at index
  const setAt = (key, idx, value) => {
    const fallback = key === 'sizes' ? [null, null, null, null]
      : key === 'shapes' ? ['door', 'door', 'door', 'door']
      : ['auto', 'auto', 'auto', 'auto'];
    const arr = [...(g[key] || fallback)];
    while (arr.length < 4) arr.push(fallback[arr.length]);
    arr[idx] = value;
    update({ [key]: arr });
  };

  return (
    <div className="admin-section">
      <h3 className="admin-section-title">Qapılar (4 sol + 4 sağ)</h3>
      <p className="admin-section-desc">
        Qapı dəyərləri doğum tarixindən hesablanır. Hər qapı üçün şəkil, ölçü və dəyər mənbəyini ayrıca seçə bilərsiniz.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!g.enabled} onChange={(e) => update({ enabled: e.target.checked })} data-testid="gates-enabled-toggle" />
          <span className="text-sm text-white/80">Qapıları göstər</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={g.show_left !== false} onChange={(e) => update({ show_left: e.target.checked })} />
          <span className="text-sm text-white/80">Sol qapıları göstər</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={g.show_right !== false} onChange={(e) => update({ show_right: e.target.checked })} />
          <span className="text-sm text-white/80">Sağ qapıları göstər</span>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-sm text-white/80 w-16">Rəng</span>
          <input type="color" value={g.color || '#fbbf24'} onChange={(e) => update({ color: e.target.value })} />
        </label>
        <label className="flex items-center gap-2 md:col-span-2">
          <span className="text-sm text-white/80 w-32">Default ölçü: {g.size || 28}px</span>
          <input type="range" min="12" max="60" step="1" value={g.size || 28}
            onChange={(e) => update({ size: parseInt(e.target.value, 10) })} className="flex-1" />
        </label>
        <label className="flex items-center gap-2 md:col-span-2"
               style={{ borderTop: '1px dashed rgba(251,191,36,0.25)', paddingTop: 8 }}>
          <span className="text-sm text-yellow-300 w-44 whitespace-nowrap">
            ∞ ölçü: {Math.round((g.infinity_scale || 1) * 100)}%
          </span>
          <input
            type="range" min="0.3" max="3" step="0.05"
            value={g.infinity_scale || 1}
            onChange={(e) => update({ infinity_scale: parseFloat(e.target.value) })}
            className="flex-1"
            data-testid="gate-infinity-scale"
          />
        </label>
      </div>

      {/* Per-gate config */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-white/80 mb-2">Hər qapı üçün ayrıca tənzimləmə</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="p-3 rounded border border-white/10 bg-black/30 flex flex-col gap-2">
              <div className="text-xs text-yellow-300/80">Qapı #{i + 1}</div>
              <input
                className="karma-input text-left"
                value={(g.names || [])[i] || ''}
                placeholder={`Adı (məs: 0-30)`}
                onChange={(e) => {
                  const names = [...(g.names || ['', '', '', ''])];
                  while (names.length < 4) names.push('');
                  names[i] = e.target.value;
                  update({ names });
                }}
                data-testid={`gate-name-${i}`}
              />
              <select
                className="karma-input"
                value={(g.shapes || [])[i] || 'door'}
                onChange={(e) => setAt('shapes', i, e.target.value)}
                data-testid={`gate-shape-${i}`}
              >
                {SHAPE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/60 whitespace-nowrap">
                  Ölçü: {(g.sizes || [])[i] || `${g.size || 28} (default)`}
                </span>
                <input
                  type="range" min="0" max="80" step="2"
                  value={(g.sizes || [])[i] || 0}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setAt('sizes', i, v === 0 ? null : v);
                  }}
                  className="flex-1"
                  data-testid={`gate-size-${i}`}
                />
              </div>
              <select
                className="karma-input"
                value={(g.value_sources || [])[i] || 'auto'}
                onChange={(e) => setAt('value_sources', i, e.target.value)}
                data-testid={`gate-source-${i}`}
              >
                {sourceOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {/* Per-gate visibility toggles (hide left / hide right / hide name / hide value) */}
              <div className="grid grid-cols-2 gap-1 text-[11px] text-white/70">
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={!((g.hidden_left || [])[i])}
                    onChange={(e) => {
                      const arr = [...(g.hidden_left || [false, false, false, false])];
                      while (arr.length < 4) arr.push(false);
                      arr[i] = !e.target.checked;
                      update({ hidden_left: arr });
                    }}
                    data-testid={`gate-visible-left-${i}`}
                  />
                  Sol göstər
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={!((g.hidden_right || [])[i])}
                    onChange={(e) => {
                      const arr = [...(g.hidden_right || [false, false, false, false])];
                      while (arr.length < 4) arr.push(false);
                      arr[i] = !e.target.checked;
                      update({ hidden_right: arr });
                    }}
                    data-testid={`gate-visible-right-${i}`}
                  />
                  Sağ göstər
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={!((g.hide_names || [])[i])}
                    onChange={(e) => {
                      const arr = [...(g.hide_names || [false, false, false, false])];
                      while (arr.length < 4) arr.push(false);
                      arr[i] = !e.target.checked;
                      update({ hide_names: arr });
                    }}
                    data-testid={`gate-show-name-${i}`}
                  />
                  Adı göstər
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={!((g.hide_values || [])[i])}
                    onChange={(e) => {
                      const arr = [...(g.hide_values || [false, false, false, false])];
                      while (arr.length < 4) arr.push(false);
                      arr[i] = !e.target.checked;
                      update({ hide_values: arr });
                    }}
                    data-testid={`gate-show-value-${i}`}
                  />
                  Dəyəri göstər
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview date */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-sm text-white/70">Önizləmə tarixi:</span>
        <input className="karma-input text-left" style={{ maxWidth: 160 }}
          value={previewDate} onChange={(e) => setPreviewDate(e.target.value)} placeholder="GG.AA.İİİİ" />
        {previewValues && (
          <span className="text-sm text-yellow-300">{previewValues.join(' → ')}</span>
        )}
      </div>

      {/* SVG canvas with draggable gates */}
      <div
        ref={wrapRef}
        className="position-editor-canvas"
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{
          position: 'relative',
          aspectRatio: `${VIEW_W} / ${VIEW_H}`,
          background: 'rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8,
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} style={{ width: '100%', height: '100%' }}>
          <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="transparent" />

          {/* Background layers — same render as main page (1:1 with main matrix) */}
          {(backgroundLayers || []).map((layer, i) => {
            const fit = layer.fit_mode || 'cover';
            let par = 'xMidYMid slice';
            if (fit === 'contain' || fit === 'fit') par = 'xMidYMid meet';
            else if (fit === 'stretch' || fit === 'fill') par = 'none';
            else if (fit === 'natural' || fit === 'none') par = 'xMidYMid meet';
            return (
              <g key={`gbg-${i}`}
                 transform={`translate(${layer.offset_x || 0}, ${layer.offset_y || 0}) translate(${VIEW_W / 2}, ${VIEW_H / 2}) scale(${layer.scale || 1}) translate(${-VIEW_W / 2}, ${-VIEW_H / 2})`}
                 opacity={(layer.opacity ?? 1) * 0.55}>
                <image
                  href={`${BACKEND_URL}/api/uploads/${layer.filename}`}
                  x="0" y="0" width={VIEW_W} height={VIEW_H}
                  preserveAspectRatio={par}
                />
              </g>
            );
          })}

          {/* Grid overlay — subtle, on top of bg */}
          <g opacity="0.06">
            {Array.from({ length: 13 }).map((_, i) => (
              <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2={VIEW_H} stroke="white" strokeWidth="1" />
            ))}
            {Array.from({ length: 15 }).map((_, i) => (
              <line key={`h${i}`} x1="0" y1={i * 50} x2={VIEW_W} y2={i * 50} stroke="white" strokeWidth="1" />
            ))}
          </g>
          {(g.show_left !== false) && (g.positions_left || []).map((p, i) => renderPreviewGate('left', p, i))}
          {(g.show_right !== false) && (g.positions_right || []).map((p, i) => renderPreviewGate('right', p, i))}
        </svg>
      </div>
      <p className="text-xs text-white/40 mt-2">
        Default formula (Avtomatik): 1-ci qapı = 36 − reduceToSingleDigit(doğum tarixinin bütün rəqəmlərinin cəmi).
        2-ci, 3-cü qapılar = +9 artımlarla, 4-cü qapı 3-cü ilə eyni dəyəri alır (48+ aralığı).
      </p>
    </div>
  );
};

export default GatesEditor;
